import React, {
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { changeLocation, getClientCurrentLocation } from "@/utils";
import { mapMarkerIcon } from "@/utils/constants";

interface Props {
  cityName?: string;
  areaName?: string;
  streetAddress?: string;
  className?: string;
  setClientLocation: React.Dispatch<React.SetStateAction<[number, number]>>;
  clientLocation?: [number, number];

  cords?: [number, number];
  showDirection?: boolean;
}

const GoogleMap: FC<Props> = ({
  className,
  cityName,
  areaName,
  streetAddress,
  setClientLocation,
  clientLocation = [0, 0],

  cords = [23.8041, 90.4152],
  showDirection = false,
}) => {
  const [directions, setDirections] = useState<google.maps.LatLng[] | null>(
    null
  );

  const latLng = useMemo(() => {
    return new google.maps.LatLng(cords[0], cords[1]);
  }, [cords]);

  useEffect(() => {
    // Use the DirectionsService to request directions
    const directionsService = new window.google.maps.DirectionsService();

    const origin: google.maps.LatLng = new google.maps.LatLng(23.8041, 90.4152);
    const destination = new google.maps.LatLng(
      clientLocation[0],
      clientLocation[1]
    );

    directionsService.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (response, status) => {
        if (status === "OK" && response?.routes?.[0]?.overview_path) {
          setDirections(response?.routes?.[0]?.overview_path || null);
        } else {
          // console.error(`Directions request failed with status: ${status}`);
        }
      }
    );
  }, [clientLocation]);

  return (
    <Map
      cords={cords}
      style={{
        height: "100%",
        width: "100%",
      }}
      setClientLocation={setClientLocation}
    >
      <Marker
        // animation={google.maps.Animation.BOUNCE}
        position={latLng}
        clickable={true}
        icon={{
          path: google.maps.SymbolPath.CIRCLE,
          url: mapMarkerIcon,
          scaledSize: new google.maps.Size(40, 40),
        }}
      />

      {/* {directions && (
                    <Polyline
                        path={directions}
                        options={{
                        strokeColor: '#0000FF',
                        strokeOpacity: 0.8,
                        strokeWeight: 4,
                        }}
                    />
                )} */}
    </Map>
  );
};

interface MapProps extends google.maps.MapOptions {
  style: { [key: string]: string };
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  setClientLocation: React.Dispatch<React.SetStateAction<[number, number]>>;
  addressText?: string;
  cords: [number, number];
  children?: ReactNode;
}

const MapComp = ({
  onClick,
  onIdle,
  setClientLocation,
  children,
  style,
  cords,
  addressText,
  ...options
}: MapProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {}));
    }
  }, [ref, map]);

  useEffect(() => {
    if (map) {
      map.setOptions({
        zoom: 16,
        center: {
          lat: 23.8041,
          lng: 90.4152,
        },
        restriction: {
          latLngBounds: {
            north: 26.6385,
            west: 88.8881,
            south: 20.7387,
            east: 92.6267,
          },
          strictBounds: true,
        },
      });

      getClientCurrentLocation().then((latLng) => {
        if (setClientLocation)
          setClientLocation(() => [latLng.lat(), latLng.lng()]);
      });
    }
  }, [map]);

  useEffect(() => {
    if (map && !!cords) {
      const latlong: google.maps.LatLng = new google.maps.LatLng(
        cords[0],
        cords[1]
      );
      if (!!cords[0] && !!cords[1]) {
        changeLocation({
          map,
          location: latlong,
          animationDuration: 2500,
        });
      } else {
        map.setCenter({ lat: cords[0], lng: cords[1] });
      }
    }
  }, [map, cords]);

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          // @ts-ignore
          return React.cloneElement(child, { map });
        }
      })}
    </>
  );
};

const Map = React.memo(MapComp);

const Marker: FC<google.maps.MarkerOptions> = (options) => {
  const [marker, setMarker] = useState<google.maps.Marker>();

  useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.Marker());
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null);
      }
    };
  }, [marker]);

  useEffect(() => {
    if (marker) {
      marker.setOptions(options);
    }
  }, [marker, options]);

  return null;
};

const DirectionsMapComp = ({ cords }: { cords: number[] }) => {
  const [map, setMap] = useState<google.maps.Map>();

  useEffect(() => {
    const mapOptions: google.maps.MapOptions = {
      zoom: 16,
      center: new google.maps.LatLng(cords[0], cords[1]),
    };

    const map = new window.google.maps.Map(
      document?.getElementById("map-direction-1212") as any,
      mapOptions
    );
    setMap(map);
  }, [cords]);

  useEffect(() => {
    const getRoute = async () => {
      if (!map || cords.length < 2) return;

      const clientLocation = await getClientCurrentLocation();

      const directionsService = new window.google.maps.DirectionsService();
      const directionsRenderer = new window.google.maps.DirectionsRenderer();

      map.setCenter(clientLocation);

      directionsRenderer.setMap(map);

      const request: google.maps.DirectionsRequest = {
        origin: clientLocation,
        destination: new google.maps.LatLng(cords[0], cords[1]),
        travelMode: window.google.maps.TravelMode.DRIVING,
      };

      directionsService.route(request, function (result, status) {
        if (status === "OK") {
          directionsRenderer.setDirections(result);
        } else {
          console.error("Directions request failed with status: " + status);
        }
      });
    };

    getRoute();
  }, [map, cords]);

  return (
    <div
      id="map-direction-1212"
      style={{ height: "100%", width: "100%" }}
    ></div>
  );
};

export const DirectionsMap = React.memo(DirectionsMapComp);

export default React.memo(GoogleMap);
